import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { useQuery } from '@apollo/client'
import { Wizard, WizardStep } from '@factorialco/deprecated-design-system'
import {
  CreateLearningPlatformNewTrainingWizardDocument,
  TrainingNewCourseWizardDocument,
} from 'generated/resources/graphql'
import moment from 'moment'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { FormErrors, useForm } from 'modules/core/hooks/useForm'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { useTrainingsCategories } from 'modules/trainings/hooks/Categories/useTrainingsCategories'
import { useCompetencies } from 'modules/trainings/hooks/Trainings/useCompetencies'
import { useCreateTraining } from 'modules/trainings/hooks/Trainings/useCreateTraining'
import { useUpsertTrainingSubsidy } from 'modules/trainings/hooks/Trainings/useUpsertTrainingSubsidy'
import { useValidation } from 'modules/trainings/hooks/useValidation'

import { BasicInfoStep } from './BasicInfoStep'
import { CompletionConfigurationStep } from './CompletionConfigurationStep'
import { InternalInfoStep } from './InternalInfoStep'
import { LearningPlatformData, TrainingWizardData } from './types'

export const NewTrainingWizard = () => {
  const { competencies } = useCompetencies()
  const { categories } = useTrainingsCategories()
  const { data } = useQuery(TrainingNewCourseWizardDocument, {
    variables: { categories: ['training'] },
  })
  const processes = data?.processes.processesConnection.nodes

  const { company, access } = useCurrentSession()
  const { create } = useCreateTraining({
    onCompleted(data) {
      if (data.trainingsMutations.createTrainingsTraining?.errors.length) {
        return
      }
    },
  })

  const [createLearningPlatform] = useMutation(CreateLearningPlatformNewTrainingWizardDocument, {
    extractorFn: (data) => data.trainingsMutations.createTrainingsLearningPlatform,
  })

  const { upsertTrainingSubsidy } = useUpsertTrainingSubsidy()

  const navigate = useNavigate()
  const { navigation } = useNavigation()
  const initialData: TrainingWizardData = {
    method: 'create',
    trainingId: '',
    name: '',
    code: '',
    description: '',
    objectives: '',
    external: false,
    externalProvider: '',
    categoryIds: [],
    workflowStatus: false,
    year: moment().year(),
    competencyIds: [],
    trainingMembershipsIds: [],
    attachments: [],
    fundaeSubsidized: false,
    subsidized: false,
    processId: undefined,
    validFor: undefined,
    totalDuration: 0,
    attendancePercentage: undefined,
    moduleCompletedPercentage: undefined,
    knowledgeTestRequired: false,
    thumbnail: undefined,
  }

  const [wizardData, setWizardData] = useState<TrainingWizardData>(initialData)
  const { validateLink, validateWorkflows } = useValidation()
  let workflowError: string | undefined = undefined
  const learningPlatformForm = useForm<LearningPlatformData>({
    defaultValues: { name: '', link: '', endDate: '', startDate: '', hasLearningPlatform: false },
    validate: (data) => {
      const errors: FormErrors<LearningPlatformData> = {}

      if (!!validateLink(data.link)) errors.link = validateLink(data.link)

      return errors
    },
    onSubmit: async () => {
      return Promise.resolve({ success: true, data: '' })
    },
    validateOnBlur: true,
  })

  const handleOnDataChanged = (newData: Partial<TrainingWizardData>) => {
    setWizardData({ ...wizardData, ...newData })
  }

  const handleSubmit = async (data: TrainingWizardData) => {
    workflowError = validateWorkflows({
      workflowStatus: data.workflowStatus,
      processId: data.processId,
    })
    if (!!workflowError) {
      return {
        success: false,
        errors: [
          {
            type: 'generic_error',
            message: i18n.t('common.mandatory'),
          },
        ],
      }
    } else {
      const hasCompletionConfig =
        data.attendancePercentage !== undefined ||
        data.moduleCompletedPercentage !== undefined ||
        data.knowledgeTestRequired

      const resultCreateTraining = await create({
        ...data,
        thumbnail: data.thumbnail,
        validFor: data.validFor,
        authorId: access.id,
        companyId: company.id,
        description: data.description || '',
        completionConfiguration: hasCompletionConfig
          ? {
              attendancePercentage: data.attendancePercentage,
              moduleCompletedPercentage: data.moduleCompletedPercentage,
              knowledgeTestRequired: data.knowledgeTestRequired,
            }
          : undefined,
      })

      const handledCreateTraining = handleResult(resultCreateTraining, () => {
        app.setFlash({
          type: 'wrong',
          message: i18n.t('trainings.create_training_modal.wrong'),
        })
      })

      if (handledCreateTraining.success) {
        const trainingId = handledCreateTraining.data.training?.id
        if (trainingId) {
          if (learningPlatformForm.values.hasLearningPlatform) {
            const response = await createLearningPlatform({
              variables: {
                ...learningPlatformForm.values,
                endDate: !!learningPlatformForm.values.endDate
                  ? learningPlatformForm.values.endDate
                  : undefined,
                startDate: !!learningPlatformForm.values.startDate
                  ? learningPlatformForm.values.startDate
                  : undefined,
                trainingId: trainingId,
                companyId: company.id,
              },
            })

            const handledCreateLearningPlatform = handleResult(response, () => {
              app.setFlash({
                type: 'wrong',
                message: i18n.t('trainings.create_training_modal.success'),
              })
            })

            if (handledCreateLearningPlatform.success) {
              app.setFlash({
                type: 'correct',
                message: i18n.t('trainings.create_training_modal.success'),
              })
            }
            learningPlatformForm.resetForm({})
          }

          const updateTrainingSubsidyResult = await upsertTrainingSubsidy({
            id: trainingId,
            subsidy: data.fundaeSubsidized ? 'fundae' : data.subsidized ? 'other' : undefined,
            processId: data.workflowStatus ? data.processId : null,
            codigoCurso: wizardData.codigoCurso,
            perfil: wizardData.perfil,
            expediente: wizardData.expediente,
          })

          handleResult(updateTrainingSubsidyResult, () => {
            app.setFlash({
              type: 'wrong',
              message: i18n.t('trainings.create_training_modal.wrong'),
            })
          })

          track(trackingEvents.training.trainingCreation.submit, {
            is_duplicate: false,
          })
          navigate(
            navigation.trainings.list.details_revamp.view.overview.fullPath({
              id: Number(handledCreateTraining?.data?.training?.id),
            })
          )
        }
      }
    }
  }

  const basicInfoStep: WizardStep<TrainingWizardData> = {
    title: i18n.t('trainings.trainings_revamp.new_training.wizard.basic_information.step_title'),
    nextText: i18n.t('common.next'),
    render: (props) => (
      <BasicInfoStep
        data={props.data}
        onDataChanged={props.onDataChanged}
        competencies={competencies || []}
        errors={learningPlatformForm.errors}
        onChangeLearningPlatform={(data) => learningPlatformForm.setValues(data, true)}
        learningPlatformData={learningPlatformForm.values}
        onSubmitLP={learningPlatformForm.handleSubmit}
      />
    ),
    isCompleted: (data) => {
      if (learningPlatformForm.values.hasLearningPlatform) {
        return (
          !!data.name &&
          learningPlatformForm.isValid &&
          !!learningPlatformForm.values.name &&
          !!learningPlatformForm.values.link
        )
      } else {
        return !!data.name
      }
    },
  }

  const internalInfoStep: WizardStep<TrainingWizardData> = {
    title: i18n.t(
      'trainings.trainings_revamp.new_training.wizard.internal_information.new_step_title'
    ),
    nextText: i18n.t('common.next'),
    render: (props) => (
      <InternalInfoStep
        data={props.data}
        onDataChanged={props.onDataChanged}
        categories={categories || []}
        processes={processes || []}
        validationError={workflowError}
      />
    ),
    isCompleted: (data) => !!data.year,
  }

  const completionConfigurationStep: WizardStep<TrainingWizardData> = {
    title: i18n.t(
      'trainings.trainings_revamp.new_training.wizard.completion_configuration.step_title'
    ),
    nextText: i18n.t('common.create'),
    render: (props) => (
      <CompletionConfigurationStep data={props.data} onDataChanged={props.onDataChanged} />
    ),
    isCompleted: () => true, // This step is optional
  }

  const steps = [basicInfoStep, internalInfoStep, completionConfigurationStep]

  return (
    <Wizard.Modal
      icon="user-interaction-review_chart"
      title={i18n.t('trainings.trainings_revamp.new_training.wizard.title')}
      steps={steps}
      initialData={wizardData}
      onSubmit={(data) => handleSubmit(data)}
      onDataChanged={handleOnDataChanged}
      onClose={() => navigate(navigation.trainings.list.index.fullPath())}
      size="large"
    />
  )
}
